import * as O from 'fp-ts/lib/Option';
import {Button, Checkbox, Drawer, FormControl, FormControlLabel, FormGroup, FormLabel, Switch} from '@material-ui/core';
import * as I from 'immutable';
import React, {useState} from 'react';
import 'Shared/components/PagedView/Table/VisibleColumnsDrawer.sass';
import {toLocalStorage, LocalStorageKey} from 'Shared/utilities/localStorage';
import {useDispatch, useSelector} from 'react-redux';
import {createSnackbar} from 'Core/ducks/snackbar';
import {ReducerState} from 'rootReducer';
import {isLoaded} from 'Core/models/Async';
import {getSavedColumnVisibilityPreferences} from 'Shared/components/PagedView/Table/Table';
import {pipe} from 'fp-ts/lib/pipeable';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

type VisibleColumnsDrawer<TData> = {
  open: boolean;
  onClose: () => void;
  onColumnVisibilityChange: (columnKey: string, isVisible: boolean) => void;
  columnsMap: I.Map<string, boolean>;
  pagedViewKey: string;
};

const saveVisibilitySettingsAsDefault = (
  userId: string,
  pagedViewKey: string,
  columnKeysToView: I.Map<string, boolean>
) => {
  const pagedViewVisibilityPreferences = pipe(
    getSavedColumnVisibilityPreferences(),
    O.getOrElse(() => ({}))
  );

  const visibilityPermissionsForCurrentUser = pagedViewVisibilityPreferences[userId];

  const updatedVisibilityPreferences = {
    ...pagedViewVisibilityPreferences,
    [userId]: {...visibilityPermissionsForCurrentUser, [pagedViewKey]: columnKeysToView},
  };

  toLocalStorage(LocalStorageKey.PagedViewVisibilityPreferences)(JSON.stringify(updatedVisibilityPreferences))();
};

const VisibleColumnsDrawer = <TData extends {}>(props: VisibleColumnsDrawer<TData>) => {
  const dispatch = useDispatch();
  const {userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const [saveDefaultsOnClose, setSaveDefaultsOnClose] = useState(false);
  const {pagedViewKey, open, onClose, columnsMap, onColumnVisibilityChange} = props;
  const hasOneRemainingVisibleColumn = columnsMap.countBy(isVisible => isVisible).get(true) === 1;

  return (
    <Drawer open={open} onClose={onClose} anchor="right">
      <div className="column-drawer">
        <FormControl className="column-drawer__visible-columns-checkboxes" component="fieldset">
          <FormLabel id="visible-columns-drawer-title" component="legend">Visible Columns</FormLabel>
          <FormGroup>
            {columnsMap
              .map((isVisible, title) => (
                <FormControlLabel
                  id={`visible-columns-drawer-label-${toKebabCase(title)}`}
                  key={`${title}`}
                  control={
                    <Checkbox
                      id={`visible-columns-drawer-checkbox-${toKebabCase(title)}`}
                      disabled={hasOneRemainingVisibleColumn && isVisible}
                      checked={isVisible}
                      onChange={e => onColumnVisibilityChange(title, e.target.checked)}
                      value={title}
                    />
                  }
                  label={title}
                />
              ))
              .toIndexedSeq()
              .toArray()}
          </FormGroup>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              id={`visible-columns-drawer-default-switch`}
              checked={saveDefaultsOnClose}
              onChange={e => setSaveDefaultsOnClose(e.target.checked)}
              color="primary"
            />
          }
          label="Save As Default"
        />
        <Button
          id={`visible-columns-drawer-preference-button`}
          color="primary"
          onClick={() => {
            onClose();

            if (saveDefaultsOnClose && isLoaded(userClaims)) {
              saveVisibilitySettingsAsDefault(userClaims.Data.id, pagedViewKey, columnsMap);
              setSaveDefaultsOnClose(false);
              dispatch(createSnackbar('Column Preferences Saved'));
            }
          }}
        >
          {saveDefaultsOnClose ? 'Close and Save Preferences' : 'Close'}
        </Button>
      </div>
    </Drawer>
  );
};

export default VisibleColumnsDrawer;
